import * as React from "react";
import { connect } from "react-redux";
import { debounce } from "es-toolkit";
import {
    UPDATE_PLANNING_TABLE_HEIGHT,
    UPDATE_PLANNING_TABLE_WIDTH,
    UPDATE_PLANNING_STOCK_REQUIREMENTS_HEIGHTS,
} from "@/modules/pg/shared/planningTable/reducers/gantt/properties";
import { ScrollHandler } from "@/modules/pg/shared/planningTable/ganttChart/scrollHandler";
import ReportLoader from "../ganttChart/ReportLoader";
import ResizableHandle from "@/modules/pg/shared/planningTable/interaction/ResizableHandle";
import { clamp } from "@/lib/util";
import { subscribe, unsubscribe, publishSync, MSG_TYPES } from "@/lib/pubSub";
import local_settings, { FIELD_GANTT_PANES_HEIGHTS } from "@/lib/localSettings";

import type {
    UpdatePlanningTableWidthAction,
    UpdatePlanningTableHeightAction,
    UpdateStockRequirementsHeightAction,
    UpdatePlanningStockHeightsAction,
} from "@/modules/pg/shared/planningTable/reducers/gantt/properties";
import type { ReduxState } from "@/modules/pg/shared/planningTable/reducers/reducers";
import { Dispatch } from "redux";

type ActionTypes =
    | UpdatePlanningTableWidthAction
    | UpdatePlanningTableHeightAction
    | UpdateStockRequirementsHeightAction
    | UpdatePlanningStockHeightsAction;

type OwnProps = {
    gantt0: React.ReactNode;
    gantt1: React.ReactNode;
    gantt2: React.ReactNode;
    count_non_empty_lines: number | null;
    num_unscheduled_lines: number;
    num_stock_requirements: number;
};

type Props = OwnProps & ReduxProps;

type State = {
    pane0_ratio: number;
    pane1_ratio: number;
};

type PaneRatios = State;

const getPaneRatios = (): PaneRatios => {
    const json = local_settings.getItem(FIELD_GANTT_PANES_HEIGHTS);
    if (json) {
        try {
            return JSON.parse(json);
        } catch (e) {
            console.log("Failed to parse pane ratios from local_settings", e);
        }
    }

    return {
        pane0_ratio: 1 / 3,
        pane1_ratio: 1 / 3,
    };
};

export class GanttPanes extends React.Component<Props, State> {
    static SCROLL_SOURCE = "waterfall-chart";

    container_el: HTMLDivElement | null = null;
    orders_el: HTMLDivElement | null = null;

    state: State = {
        pane0_ratio: 1 / 3,
        pane1_ratio: 1 / 3,
    };

    scroll_node_ref = React.createRef<HTMLDivElement>();
    safe_to_scroll_ref: React.MutableRefObject<boolean> = { current: true };

    scroll_subscription!: string;
    scroll_end_subscription!: string;
    resize_observer!: ResizeObserver;

    componentDidMount() {
        this.safe_to_scroll_ref.current = true;

        this.scroll_subscription = subscribe(
            MSG_TYPES.planning_table_align_scroll,
            (data: { top?: number; left?: number; source?: string; enforce?: boolean }) => {
                if (data.enforce || (this.props.waterfall_mode && data.source !== GanttPanes.SCROLL_SOURCE)) {
                    this.safe_to_scroll_ref.current = false;
                    this.updateScroll(...[data.top, data.left]);
                }
            }
        );
        this.scroll_end_subscription = subscribe(MSG_TYPES.planning_table_align_scroll_end, () => {
            this.safe_to_scroll_ref.current = true;
        });

        this.resize_observer = new ResizeObserver(() => {
            this.updateGanttChartsDimensions();
        });

        if (document.body) {
            this.resize_observer.observe(document.body);
        }

        const container = document.querySelector("#planning_table_parallel");
        if (container) {
            this.resize_observer.observe(container);
        }

        const ratios = getPaneRatios();
        this.setState(ratios);
    }

    componentDidUpdate(prev_props: Props) {
        if (prev_props.show_stock_requirements !== this.props.show_stock_requirements) {
            this.updateGanttChartsDimensions();
        }

        if (prev_props.show_unscheduled_orders !== this.props.show_unscheduled_orders) {
            this.updateGanttChartsDimensions();
        }

        if (prev_props.order_uuid !== this.props.order_uuid) {
            if (this.props.order_uuid) {
                ScrollHandler.scrollToOrder(this.props.order_uuid);
            }
        }

        if (this.scroll_node_ref.current) {
            ScrollHandler.updateChartHorizontalScroll();
        }
    }

    componentWillUnmount() {
        if (this.resize_observer) {
            this.resize_observer.disconnect();
        }
        unsubscribe(this.scroll_subscription);
        unsubscribe(this.scroll_end_subscription);
    }

    savePaneRatios(): void {
        const { pane0_ratio, pane1_ratio } = this.state;
        local_settings.setItem(FIELD_GANTT_PANES_HEIGHTS, JSON.stringify({ pane0_ratio, pane1_ratio }));
    }

    handlePane0Resize = (movementY: number) => {
        const parent_height = this.getHeight(this.container_el);

        const min = 0;
        const max = parent_height - ResizableHandle.WIDTH * 4; // 2 handles & scroll bars
        const new_pane0_height = clamp(this.state.pane0_ratio * parent_height + movementY, min, max);

        // Make sure the other pane is not too small
        this.setState({ pane0_ratio: clamp(new_pane0_height / parent_height, 0, 0.9) });
        this.updateGanttChartsDimensions();
    };

    handlePane1Resize = (movementY: number) => {
        const parent_height = this.getHeight(this.orders_el);

        const min = ResizableHandle.WIDTH * 2;
        const max = parent_height - ResizableHandle.WIDTH * 2; // 1 handle & scroll bar
        const new_pane1_height = clamp(this.state.pane1_ratio * parent_height + movementY, min, max);

        // Make sure the other pane is not too small
        this.setState({ pane1_ratio: clamp(new_pane1_height / parent_height, 0, 0.9) });
        this.updateGanttChartsDimensions();
    };

    updateGanttChartsDimensions = debounce(() => {
        const count_non_empty_lines = this.props.count_non_empty_lines;
        const num_unscheduled_lines = this.props.num_unscheduled_lines;
        if (count_non_empty_lines) {
            this.props.reduxDispatch({
                type: UPDATE_PLANNING_TABLE_HEIGHT,
                data: {
                    count_non_empty_lines,
                    num_unscheduled_lines,
                },
            });

            this.props.reduxDispatch({
                type: UPDATE_PLANNING_TABLE_WIDTH,
                data: undefined,
            });

            if (this.props.show_stock_requirements) {
                this.props.reduxDispatch({
                    type: UPDATE_PLANNING_STOCK_REQUIREMENTS_HEIGHTS,
                    data: {
                        count_non_empty_lines,
                        num_stock_requirements: this.props.num_stock_requirements,
                    },
                });
            }
        }
    }, 150);

    getHeight = (el: HTMLDivElement | null) => {
        if (el === null) {
            return 0;
        }

        return el.getBoundingClientRect().height;
    };

    getPaneHeightStyle = (ratio: number): string => {
        return `${ratio * 100}%`;
    };

    last_scroll_x = 0;
    last_scroll_y = 0;

    getElementScrollLeft = (element: HTMLElement | Window): number => {
        const scroll_x = element instanceof Window ? element.scrollX : element.scrollLeft;
        return Math.max(0, scroll_x);
    };

    translateLineLabelsGroups = (x: number) => {
        const line_labels_group = document.querySelector("#line-labels-group") as HTMLElement;
        const style_filter =
            x + (this.props.max_line_label_length ?? 0) > (this.props.bars_start ?? 0)
                ? "drop-shadow(2px 0px 2px rgba(0, 0, 0, 0.16)"
                : "";
        if (line_labels_group) {
            line_labels_group.setAttribute("transform", `translate(${x}, 0)`);
            line_labels_group.style.filter = style_filter;
        }

        const line_labels_group_unscheduled = document.querySelector("#line-labels-group-unscheduled") as HTMLElement;
        if (line_labels_group_unscheduled) {
            line_labels_group_unscheduled.setAttribute("transform", `translate(${x}, 0)`);
            line_labels_group_unscheduled.style.filter = style_filter;
        }
    };

    updateScroll(top?: number, left?: number) {
        if (this.scroll_node_ref.current) {
            if (top !== undefined) {
                this.scroll_node_ref.current.scrollTop = top;
            }
            if (left !== undefined) {
                this.scroll_node_ref.current.scrollLeft = left;
            }
        }
    }

    onScroll = (event: React.UIEvent<HTMLElement>) => {
        event.stopPropagation();
        event.preventDefault();

        if (!event) return;

        ScrollHandler.setChartScrollLeft(event.currentTarget.scrollLeft);

        const header_shifts_group = document.querySelector("#pane1 #grid-groups-header-shifts") as HTMLElement;
        const correction = 0;
        const el = event.currentTarget;
        const scroll_top = el instanceof Window ? el.scrollY - correction : el.scrollTop;

        let y = scroll_top;
        if (y < 0) {
            y = 0;
        }

        if (this.props.waterfall_mode && this.safe_to_scroll_ref.current) {
            publishSync(MSG_TYPES.planning_table_align_scroll, { top: y, source: GanttPanes.SCROLL_SOURCE });
        }

        const translate_y = `translate(0, ${y})`;

        const pane1 = document.querySelector("#pane1");
        if (pane1 && header_shifts_group && this.last_scroll_y !== pane1.scrollTop) {
            this.last_scroll_y = pane1.scrollTop;
            header_shifts_group.setAttribute("transform", translate_y);
            header_shifts_group.style.filter = y > 0 ? "drop-shadow(0px 2px 2px rgba(0, 0, 0, 0.16)" : "";
        }

        const x = this.getElementScrollLeft(el);

        if (this.last_scroll_x === x) {
            return;
        }

        this.last_scroll_x = x;

        const pane0 = document.querySelector("#pane0");
        if (pane0) {
            pane0.scrollLeft = x;
        }

        const pane2 = document.querySelector("#pane2 .scroll-container");
        if (pane2) {
            pane2.scrollLeft = x;
        }

        this.translateLineLabelsGroups(x);
    };

    onScrollEnd = debounce(() => {
        publishSync(MSG_TYPES.planning_table_align_scroll_end);
    }, 100);

    onScrollUnscheduled = (event: React.UIEvent<HTMLElement>) => {
        const x = this.getElementScrollLeft(event.currentTarget);

        if (this.last_scroll_x === x) {
            return;
        }

        this.last_scroll_x = x;

        const pane0 = document.querySelector("#pane0");
        if (pane0) {
            pane0.scrollLeft = x;
        }

        const pane1 = document.querySelector("#pane1");
        if (pane1) {
            pane1.scrollLeft = x;
        }

        this.translateLineLabelsGroups(x);
    };

    onScrollStockRequirement = (event: React.UIEvent<HTMLElement>) => {
        const x = this.getElementScrollLeft(event.currentTarget);

        if (this.last_scroll_x === x) {
            return;
        }

        this.last_scroll_x = x;

        const pane1 = document.querySelector("#pane1");
        if (pane1) {
            pane1.scrollLeft = x;
        }

        const pane2 = document.querySelector("#pane2 .scroll-container");
        if (pane2) {
            pane2.scrollLeft = x;
        }

        this.translateLineLabelsGroups(x);
    };

    renderStockRequirements = () => {
        return (
            <div
                id="pane0"
                className="pane1"
                onScroll={this.onScrollStockRequirement}
                style={{
                    height: this.getPaneHeightStyle(this.state.pane0_ratio),
                    overflow: "auto",
                }}
            >
                {this.props.gantt0}
            </div>
        );
    };

    renderOrders = () => {
        return (
            <div
                ref={el => {
                    this.orders_el = el;
                }}
                style={{
                    height: this.props.show_stock_requirements
                        ? this.getPaneHeightStyle(1 - this.state.pane0_ratio)
                        : "100%",
                }}
            >
                <div
                    id="pane1"
                    className="pane1 js-chart-scrollable"
                    ref={this.scroll_node_ref}
                    onScroll={e => {
                        this.onScroll(e);
                        this.onScrollEnd();
                    }}
                    style={{
                        height: this.props.show_unscheduled_orders
                            ? this.getPaneHeightStyle(this.state.pane1_ratio)
                            : "100%",
                        overflow: "auto",
                        userSelect: "none",
                    }}
                >
                    {this.props.gantt1}
                </div>

                {this.props.show_unscheduled_orders && (
                    <>
                        <ResizableHandle onMove={this.handlePane1Resize} onMoveStop={() => this.savePaneRatios()} />

                        <div
                            id="pane2"
                            className="pane2"
                            style={{
                                height: this.getPaneHeightStyle(1 - this.state.pane1_ratio),
                                overflow: "auto",
                                backgroundColor: "white",
                            }}
                        >
                            <div
                                className="scroll-container"
                                onScroll={this.onScrollUnscheduled}
                                style={{
                                    height: "100%",
                                    width: "100%",
                                    overflow: "auto",
                                }}
                            >
                                {this.props.gantt2}
                            </div>
                        </div>
                    </>
                )}
            </div>
        );
    };

    render() {
        return (
            <div
                className="App h-100 position-relative"
                ref={el => {
                    this.container_el = el;
                }}
            >
                <ReportLoader />
                {this.props.show_stock_requirements && (
                    <>
                        {this.renderStockRequirements()}
                        <ResizableHandle onMove={this.handlePane0Resize} onMoveStop={() => this.savePaneRatios()} />
                    </>
                )}
                {this.renderOrders()}
            </div>
        );
    }
}

const mapState = (state: ReduxState) => {
    return {
        show_stock_requirements: state.gantt_chart_planning_table.show_stock_requirements,
        show_unscheduled_orders: state.gantt_chart_planning_table.show_unscheduled_orders,
        max_line_label_length: state.gantt_chart_properties.max_line_label_length,
        bars_start: state.gantt_chart_properties.bars_start,
        order_uuid: state.gantt_chart_filters.reschedule_order_uuid,
        waterfall_mode: state.gantt_chart_properties.waterfall_mode,
    };
};

const mapDispatch = (dispatch: Dispatch<ActionTypes>) => ({
    reduxDispatch: dispatch,
});

type ReduxProps = ReturnType<typeof mapState> & ReturnType<typeof mapDispatch>;
export default connect<ReturnType<typeof mapState>, ReturnType<typeof mapDispatch>, OwnProps, ReduxState>(
    mapState,
    mapDispatch
)(GanttPanes);
